import axios, { AxiosError } from "axios";
import urlJoin from "url-join";
import LanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/LanguagePack";
import Task from "../../../../../lib/html5-software-library/src/ranj/util/executor/Task";
import Config from "../../../core/config/Config";
import SystemMessageBuilder from "../../../core/system-message/controller/SystemMessageBuilder";
import SystemMessageEvent from "../../../core/system-message/model/SystemMessageEvent";
import NGTConfigKey from "../../../ngt/base/NGTConfigKey";
import DebugModel from "../../debug/model/DebugModel";
import PlayerModel from "../../player/model/PlayerModel";
import PlatformUserService from "../controller/PlatformUserService";
import PlatformUserModel from "../model/PlatformUserModel";

export default class AuthenticatePlatformTask extends Task {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher
     * @param {PlatformUserService} platformUserService
     * @param {PlatformUserModel} platformUserModel
     * @param {Config} config
     * @param {LanguagePack} languagePack
     * @param {PlayerModel} playerModel
     */
    constructor(dispatcher, platformUserService, platformUserModel, config, languagePack, playerModel) {
        super();

        this._dispatcher = dispatcher;
        this._platformUserService = platformUserService;
        this._platformUserModel = platformUserModel;
        this._config = config;
        this._languagePack = languagePack;
        this._playerModel = playerModel;

        this._platformGameApiEndpoint = this._config.getValue(NGTConfigKey.PLATFORM_API_ENDPOINT);
        this._apiVersion = this._config.getValue(NGTConfigKey.PLATFORM_API_VERSION);
    }

    async execute(callback) {
        if (DebugModel.mockPlatform) {
            console.warn(`[${AuthenticatePlatformTask.name}] Mock platform integration enabled. Skipping authentication.`);

            callback();
            return;
        }

        if (this._config.getValue(NGTConfigKey.PLATFORM_INTEGRATION_ENABLED) === false) {
            console.warn(`[${AuthenticatePlatformTask.name}] Platform integration is disabled via config. Skipping authentication.`);

            callback();
            return;
        }

        try {
            await this.authenticateWithPlatform();
        } catch (error) {
            /** @type {AxiosError} */
            const axiosError = error;

            const systemMessage = new SystemMessageBuilder()
                .setTitle(this._languagePack.getText(`platformErrors.gameSessionError.title`))
                .setDescription(`${this._languagePack.getText(`platformErrors.gameSessionError.description`)}`)
                .build();

            const evt = new SystemMessageEvent(SystemMessageEvent.OPEN, systemMessage);
            this._dispatcher.dispatch(evt.type, { data: evt });

            throw error;
        }

        callback();
    }

    async authenticateWithPlatform() {
        const url = new URL(window.location.href);
        const sessionId = url.searchParams.get('sessionId');
        if (!sessionId) {
            throw new Error('Session ID is missing in the URL query parameters.');
        }

        const hash = window.location.hash;
        const fragmentString = hash.slice(1); // Remove the leading '#'
        const fragments = new URLSearchParams(fragmentString);
        const accessToken = fragments.get('access_token');
        if (!accessToken) {
            throw new Error('Access token is missing in the URL fragment.');
        }

        // TODO: verify access_token against Entra ID.
        // TODO

        // Configures platform user service.
        this._platformUserService.setAccessToken(accessToken);

        // Initialize platform user model with data from the platform API.
        const platformGameApiClient = axios.create({
            baseURL: urlJoin(this._platformGameApiEndpoint, this._apiVersion),
            headers: {
                Authorization: `Bearer ${accessToken}`,
            }
        });

        const gameSessionResponse = await platformGameApiClient.get(`game-sessions/${sessionId}`);

        // Populate user model.
        const { learnerId, status, name } = gameSessionResponse.data;
        this._platformUserModel.id = learnerId;
        this._platformUserModel.name = name;
        this._platformUserModel.completionStatus = status;
        this._platformUserModel.sessionId = sessionId;

        // Legacy: also set player name in player model.
        this._playerModel.playerName = name;

        // Store in session storage.
        // sessionStorage.setItem('sessionId', sessionId);
        // sessionStorage.setItem('accessToken', accessToken);
    }
}
